import type { KeyedMutator } from 'swr';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import { type ErrorCustom, get } from '@/utils/client';
// utils
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type { SocialProfileType } from '@/types/socialProfileType';
// Types
import type { PinterestBoardType } from '@/lib/pinterestBoards/types';

import { PINTEREST_BOARDS } from '@/lib/routes/apiEndpoints';

export type mutatePinterestBoardsType = KeyedMutator<PinterestBoardsDataType[]>;

export type PinterestBoardsDataType = {
    data: PinterestBoardType[];
    cursor: string;
};

type UsePinterestBoardsProps = {
    activeSPID: SocialProfileType['id'];
    limit?: number;
    params?: {
        name?: string;
    };
    options?: SWRInfiniteConfiguration<PinterestBoardsDataType, ErrorCustom>;
    cancelToken?: AbortController;
};

const getKey = (
    pageIndex: number,
    previousPageData: PinterestBoardsDataType,
    activeSPID: SocialProfileType['id'],
    limit: number,
    params: UsePinterestBoardsProps['params']
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // First page, we don't have `previousPageData`
    const url = `${injectEndpointUrlParams(PINTEREST_BOARDS, {
        SPID: activeSPID
    })}?per_page=${limit}`;
    if (pageIndex === 0) return [url, JSON.stringify(params)];

    // Add the cursor to the API endpoint
    return [`${url}&cursor=${previousPageData.cursor}`, JSON.stringify(params)];
};

export const usePinterestBoards = ({
    activeSPID,
    limit = 20,
    params = {},
    options = {},
    cancelToken
}: UsePinterestBoardsProps) => {
    const {
        data: pinterestBoards,
        size,
        setSize,
        isLoading,
        mutate: mutatePinterestBoards,
        error: pinterestBoardsError
    } = useSWRInfinite<PinterestBoardsDataType, ErrorCustom>(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSPID, limit, params),
        ([url]: string[]) =>
            get(url, params, { signal: cancelToken?.signal }).then(
                (res: { data: PinterestBoardsDataType }) => res.data
            ),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const isLoadingMore =
        size > 0 && pinterestBoards && typeof pinterestBoards[size - 1] === 'undefined';

    return {
        dataPinterestBoards: pinterestBoards,
        mutatePinterestBoards,
        pinterestBoardsError,
        loadMorePinterestBoards: () => setSize(size + 1), // Function to load more boards (load more button)
        isLoadingInitialData: isLoading, // Check if it's the initial loading (not the load more)
        isLoadMore:
            pinterestBoards &&
            typeof pinterestBoards[pinterestBoards.length - 1]?.cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};
